import React, { useState, useEffect, useMemo } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUnreadNotifications, useMarkNotificationRead } from '@/hooks/useNotifications';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  MapPin,
  Package,
  ShoppingCart,
  Truck,
  Users,
  BarChart3,
  Settings,
  Menu,
  Bell,
  ChevronDown,
  LogOut,
  User,
  Building2,
  Keyboard,
  CheckCircle,
  AlertTriangle,
  PackagePlus,
  Wallet,
  RefreshCw,
  Clock,
  RotateCcw,
  FileText,
  FileBadge2,
  Smartphone,
  TrendingUp,
  Wrench,
  Barcode,
  ClipboardList,
  AlertCircle,
  DollarSign,
  Layers,
  Shield,
  Webhook,
  LayoutGrid,
  FileSpreadsheet,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { CommandPalette } from '@/components/ui/command-dialog';

const navigationItems = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Locations', href: '/locations', icon: MapPin },
  { name: 'Items', href: '/items', icon: Package },
  { name: 'Inventory', href: '/inventory', icon: Package },
  { name: 'Serial Numbers', href: '/serial-numbers', icon: Barcode },
  { name: 'Stock Count', href: '/stock-count', icon: ClipboardList },
  { name: 'Requisitions', href: '/requisitions', icon: ShoppingCart },
  { name: 'Approvals', href: '/approvals', icon: CheckCircle },
  { name: 'Purchase Orders', href: '/purchase-orders', icon: Truck },
  { name: 'Budget', href: '/budget', icon: Wallet },
  { name: 'Auto-Reorder', href: '/auto-reorder', icon: RefreshCw },
  { name: 'Vendors', href: '/vendors', icon: Users },
  { name: 'Expiry Tracking', href: '/expiry', icon: Clock },
  { name: 'Returns', href: '/returns', icon: RotateCcw },
  { name: 'Recalls', href: '/recalls', icon: AlertCircle },
  { name: 'Audit Log', href: '/audit-log', icon: FileText },
  { name: 'Contracts', href: '/contracts', icon: FileBadge2 },
  { name: 'Currencies', href: '/currencies', icon: DollarSign },
  { name: 'Assembly', href: '/assembly', icon: Layers },
  { name: 'Consumption', href: '/consumption', icon: TrendingUp },
  { name: 'Equipment', href: '/equipment', icon: Wrench },
  { name: 'Quality Control', href: '/quality-control', icon: Shield },
  { name: 'Analytics', href: '/analytics', icon: BarChart3 },
  { name: 'Mobile', href: '/mobile-inventory', icon: Smartphone },
  { name: 'Reports', href: '/reports', icon: BarChart3 },
  { name: 'Dashboard Custom', href: '/dashboard-customization', icon: LayoutGrid },
  { name: 'Data Export/Import', href: '/data-export-import', icon: FileSpreadsheet },
  { name: 'Webhooks', href: '/webhooks', icon: Webhook },
  { name: 'Security & MFA', href: '/security', icon: Shield },
  { name: 'Settings', href: '/settings', icon: Settings },
];

// Breadcrumb mapping
const breadcrumbMap: Record<string, { title: string; href?: string }[]> = {
  '/': [{ title: 'Dashboard', href: '/' }],
  '/locations': [
    { title: 'Dashboard', href: '/' },
    { title: 'Locations', href: '/locations' },
  ],
  '/items': [
    { title: 'Dashboard', href: '/' },
    { title: 'Items', href: '/items' },
  ],
  '/inventory': [
    { title: 'Dashboard', href: '/' },
    { title: 'Inventory', href: '/inventory' },
  ],
  '/requisitions': [
    { title: 'Dashboard', href: '/' },
    { title: 'Requisitions', href: '/requisitions' },
  ],
  '/approvals': [
    { title: 'Dashboard', href: '/' },
    { title: 'Approvals', href: '/approvals' },
  ],
  '/purchase-orders': [
    { title: 'Dashboard', href: '/' },
    { title: 'Purchase Orders', href: '/purchase-orders' },
  ],
  '/expiry': [
    { title: 'Dashboard', href: '/' },
    { title: 'Expiry Tracking', href: '/expiry' },
  ],
  '/returns': [
    { title: 'Dashboard', href: '/' },
    { title: 'Returns', href: '/returns' },
  ],
  '/audit-log': [
    { title: 'Dashboard', href: '/' },
    { title: 'Audit Log', href: '/audit-log' },
  ],
  '/vendors': [
    { title: 'Dashboard', href: '/' },
    { title: 'Vendors', href: '/vendors' },
  ],
  '/reports': [
    { title: 'Dashboard', href: '/' },
    { title: 'Reports', href: '/reports' },
  ],
  '/settings': [
    { title: 'Dashboard', href: '/' },
    { title: 'Settings', href: '/settings' },
  ],
  '/settings/inventory': [
    { title: 'Dashboard', href: '/' },
    { title: 'Settings', href: '/settings' },
    { title: 'Inventory', href: '/settings/inventory' },
  ],
  '/settings/purchase': [
    { title: 'Dashboard', href: '/' },
    { title: 'Settings', href: '/settings' },
    { title: 'Purchase', href: '/settings/purchase' },
  ],
  '/settings/approvals': [
    { title: 'Dashboard', href: '/' },
    { title: 'Settings', href: '/settings' },
    { title: 'Approvals', href: '/settings/approvals' },
  ],
  '/settings/general': [
    { title: 'Dashboard', href: '/' },
    { title: 'Settings', href: '/settings' },
    { title: 'General', href: '/settings/general' },
  ],
  '/contracts': [
    { title: 'Dashboard', href: '/' },
    { title: 'Contracts', href: '/contracts' },
  ],
  '/analytics': [
    { title: 'Dashboard', href: '/' },
    { title: 'Analytics', href: '/analytics' },
  ],
  '/mobile-inventory': [
    { title: 'Dashboard', href: '/' },
    { title: 'Mobile Inventory', href: '/mobile-inventory' },
  ],
  '/consumption': [
    { title: 'Dashboard', href: '/' },
    { title: 'Consumption', href: '/consumption' },
  ],
  '/equipment': [
    { title: 'Dashboard', href: '/' },
    { title: 'Equipment', href: '/equipment' },
  ],
  '/serial-numbers': [
    { title: 'Dashboard', href: '/' },
    { title: 'Serial Numbers', href: '/serial-numbers' },
  ],
  '/stock-count': [
    { title: 'Dashboard', href: '/' },
    { title: 'Stock Count', href: '/stock-count' },
  ],
  '/recalls': [
    { title: 'Dashboard', href: '/' },
    { title: 'Recalls', href: '/recalls' },
  ],
  '/currencies': [
    { title: 'Dashboard', href: '/' },
    { title: 'Currencies', href: '/currencies' },
  ],
  '/assembly': [
    { title: 'Dashboard', href: '/' },
    { title: 'Assembly', href: '/assembly' },
  ],
  '/quality-control': [
    { title: 'Dashboard', href: '/' },
    { title: 'Quality Control', href: '/quality-control' },
  ],
  '/security': [
    { title: 'Dashboard', href: '/' },
    { title: 'Security & MFA', href: '/security' },
  ],
  '/webhooks': [
    { title: 'Dashboard', href: '/' },
    { title: 'Webhooks', href: '/webhooks' },
  ],
  '/dashboard-customization': [
    { title: 'Dashboard', href: '/' },
    { title: 'Dashboard Customization', href: '/dashboard-customization' },
  ],
  '/data-export-import': [
    { title: 'Dashboard', href: '/' },
    { title: 'Data Export/Import', href: '/data-export-import' },
  ],
};

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Restore preference from localStorage
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Get unread notifications for current user
  const { data: unreadNotifications = [] } = useUnreadNotifications();
  const markReadMutation = useMarkNotificationRead();

  // Persist sidebar state
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', sidebarCollapsed.toString());
  }, [sidebarCollapsed]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K for search focus
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        const searchInput = document.getElementById('global-search') as HTMLInputElement;
        searchInput?.focus();
      }
      // Escape to close mobile menu
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const breadcrumbs = useMemo(() => {
    const path = location.pathname;
    const base = breadcrumbMap[path] || [{ title: 'Dashboard', href: '/' }];
    // Find the most specific match for sub-routes
    const matched = Object.entries(breadcrumbMap).find(([key]) => path.startsWith(key));
    return matched ? breadcrumbMap[matched[0]] : base;
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background flex">
      {/* Skip link for accessibility */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-screen border-r border-border/60 bg-surface transition-all duration-300 ease-in-out transform shadow-xl',
          sidebarCollapsed ? '-translate-x-full' : 'translate-x-0',
          mobileMenuOpen ? 'translate-x-0' : '',
          'w-64' // Default width
        )}
      >
        {/* Logo */}
        <div className="flex items-center h-16 px-6 border-b border-border/60 bg-gradient-to-b from-neutral-50 to-surface">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-600/10 rounded-xl shadow-sm">
              <Building2 className="h-7 w-7 text-primary-600" />
            </div>
            {!sidebarCollapsed && (
              <span className="text-lg font-display font-semibold text-text-primary tracking-tight">
                Inventory Pro
              </span>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" aria-label="Main navigation">
          {navigationItems.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                cn(
                  'flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 group',
                  isActive
                    ? 'bg-primary-50 text-primary-700 shadow-sm'
                    : 'text-text-secondary hover:bg-neutral-50 hover:text-text-primary'
                )
              }
            >
              <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
              {!sidebarCollapsed && <span>{item.name}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Sidebar footer with user info */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-border/60 p-4 bg-gradient-to-t from-neutral-50 to-surface">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="relative">
              <Avatar className="h-10 w-10 ring-2 ring-background">
                <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                  {user?.full_name ? getInitials(user.full_name) : 'US'}
                </AvatarFallback>
              </Avatar>
              {/* Online status indicator */}
              <span className="absolute bottom-0 right-0 h-3 w-3 bg-green-500 border-2 border-white rounded-full" />
            </div>
            {!sidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-text-primary truncate">
                  {user?.full_name || 'User'}
                </p>
                <p className="text-xs text-text-muted truncate capitalize">
                  {user?.role?.replace('_', ' ') || 'Staff'}
                </p>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              aria-label="Sign out"
              className="h-8 w-8 text-text-muted hover:text-red-600"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className={cn(
        'flex-1 transition-all duration-300',
        sidebarCollapsed ? 'lg:pl-0' : 'lg:pl-64'
      )}>
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-surface/95 backdrop-blur-sm border-b border-border/60 shadow-sm">
          <div className="flex items-center h-full px-4 gap-4">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden"
              aria-label="Toggle menu"
            >
              <Menu className="h-5 w-5" />
            </Button>

            {/* Desktop collapse button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="hidden lg:flex"
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <Menu className="h-5 w-5" />
            </Button>

            {/* Search bar with keyboard shortcut hint */}
            <div className="flex-1 max-w-2xl">
              <CommandPalette />
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {/* Notifications with badge and dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Notifications" className="relative">
                  <Bell className="h-5 w-5" />
                  {unreadNotifications.length > 0 && (
                    <Badge
                      variant="error"
                      className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                    >
                      {unreadNotifications.length > 9 ? '9+' : unreadNotifications.length}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
                <DropdownMenuLabel className="flex items-center justify-between">
                  <span>Notifications</span>
                  {unreadNotifications.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs"
                      onClick={async () => {
                        await Promise.all(
                          unreadNotifications
                            .filter((n) => !n.is_read)
                            .map((n) => markReadMutation.mutateAsync(n.notification_id!))
                        );
                      }}
                    >
                      Mark all read
                    </Button>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {unreadNotifications.length === 0 ? (
                  <div className="p-4 text-center text-sm text-text-muted">
                    No unread notifications
                  </div>
                ) : (
                  unreadNotifications.slice(0, 10).map((notification) => (
                    <DropdownMenuItem
                      key={notification.notification_id}
                      className="flex flex-col items-start gap-1 p-3 cursor-pointer"
                      onClick={async () => {
                        if (notification.notification_id) {
                          await markReadMutation.mutateAsync(notification.notification_id);
                        }
                        if (notification.related_entity_type === 'requisition' && notification.related_entity_id) {
                          navigate(`/requisitions/${notification.related_entity_id}`);
                        }
                      }}
                    >
                      <div className="flex items-start gap-2 w-full">
                        <AlertTriangle className="h-4 w-4 text-warning-dark shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-text-primary truncate">
                            {notification.title}
                          </p>
                          <p className="text-xs text-text-muted line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-text-muted mt-1">
                            {new Date(notification.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>

              <Separator orientation="vertical" className="h-6 mx-1" />

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 px-2">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs bg-primary-100 text-primary-700">
                        {user?.full_name ? getInitials(user.full_name) : 'US'}
                      </AvatarFallback>
                    </Avatar>
                    <ChevronDown className="h-4 w-4 text-text-muted hidden sm:block" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-semibold text-text-primary">{user?.full_name || 'User'}</p>
                      <p className="text-xs text-text-muted">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings')}>
                    <PackagePlus className="mr-2 h-4 w-4" />
                    Inventory Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Keyboard className="mr-2 h-4 w-4" />
                    Keyboard shortcuts
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-error-dark">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Breadcrumb */}
        {breadcrumbs.length > 1 && (
          <div className="border-b border-border bg-background">
            <div className="px-6 py-3">
              <nav className="flex items-center space-x-2 text-sm" aria-label="Breadcrumb">
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && (
                      <ChevronDown className="h-3 w-3 text-text-muted rotate-[-90deg]" />
                    )}
                    {crumb.href ? (
                      <NavLink
                        to={crumb.href}
                        className="text-text-muted hover:text-primary transition-colors"
                      >
                        {crumb.title}
                      </NavLink>
                    ) : (
                      <span className="font-medium text-text-primary">{crumb.title}</span>
                    )}
                  </React.Fragment>
                ))}
              </nav>
            </div>
          </div>
        )}

        {/* Main content */}
        <main id="main-content" className="min-h-screen pt-4 pb-8">
          <div className="px-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}
